import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  AlertCircle,
  Search,
  Edit,
  Trash2,
  BookOpen,
  Users,
  MoreVertical,
  Play,
  Pause,
  UserCheck,
} from "lucide-react";
import { eCourseService } from "../../services/elearning/eCourseService";
import { userService } from "../../services/users";
import type { ECourse } from "../../services/elearning/eCourseService";
import type { User } from "../../types/user";

interface AdminCourse extends ECourse {
  approvalHistory?: Array<{
    action: string;
    performedBy: {
      firstName: string;
      lastName: string;
    };
    performedAt: string;
    notes?: string;
  }>;
}

const AdminECourseApprovals: React.FC = () => {
  const [courses, setCourses] = useState<AdminCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedCourse, setSelectedCourse] = useState<AdminCourse | null>(
    null
  );
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>("");
  const [courseToReassign, setCourseToReassign] = useState<AdminCourse | null>(
    null
  );

  const fetchAllCourses = async () => {
    try {
      setLoading(true);
      const response = await eCourseService.getAllCourses({
        limit: 100, // Get more courses for admin view
        status: statusFilter !== "all" ? statusFilter : undefined,
      });

      if (response.success) {
        setCourses(response.data.courses || []);
      } else {
        setError("Failed to fetch courses");
      }
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError("Failed to fetch courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCourses();
  }, [statusFilter]);

  const handleApprove = async (courseId: string) => {
    try {
      const response = await eCourseService.approveCourse(
        courseId,
        approvalNotes
      );
      if (response.success) {
        setShowApprovalModal(false);
        setSelectedCourse(null);
        setApprovalNotes("");
        fetchAllCourses();
      } else {
        setError("Failed to approve course");
      }
    } catch (err) {
      console.error("Error approving course:", err);
      setError("Failed to approve course");
    }
  };

  const handleReject = async (courseId: string) => {
    try {
      const response = await eCourseService.rejectCourse(
        courseId,
        approvalNotes
      );
      if (response.success) {
        setShowApprovalModal(false);
        setSelectedCourse(null);
        setApprovalNotes("");
        fetchAllCourses();
      } else {
        setError("Failed to reject course");
      }
    } catch (err) {
      console.error("Error rejecting course:", err);
      setError("Failed to reject course");
    }
  };

  const handlePublish = async (courseId: string) => {
    try {
      const response = await eCourseService.publishCourse(courseId);
      if (response.success) {
        fetchAllCourses();
      } else {
        setError("Failed to publish course");
      }
    } catch (err) {
      console.error("Error publishing course:", err);
      setError("Failed to publish course");
    }
  };

  const handleUnpublish = async (courseId: string) => {
    try {
      const response = await eCourseService.unpublishCourse(courseId);
      if (response.success) {
        fetchAllCourses();
      } else {
        setError("Failed to unpublish course");
      }
    } catch (err) {
      console.error("Error unpublishing course:", err);
      setError("Failed to unpublish course");
    }
  };

  const handleDelete = async (courseId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this course? This action cannot be undone."
      )
    )
      return;

    try {
      const response = await eCourseService.deleteCourse(courseId);
      if (response.success) {
        fetchAllCourses();
      } else {
        setError("Failed to delete course");
      }
    } catch (err) {
      console.error("Error deleting course:", err);
      setError("Failed to delete course");
    }
  };

  const handleReassignClick = async (course: AdminCourse) => {
    setCourseToReassign(course);
    setShowReassignModal(true);

    // Fetch teachers if not already loaded
    if (teachers.length === 0) {
      try {
        console.log("Fetching teachers...");
        const response = await userService.getUsers({ role: "teacher" });
        console.log("Teachers response:", response);
        if (response.success) {
          setTeachers(response.data || []);
        } else {
          console.error("Failed to fetch teachers:", response);
        }
      } catch (err) {
        console.error("Error fetching teachers:", err);
      }
    }
  };

  const handleReassignCourse = async () => {
    if (!courseToReassign || !selectedTeacherId) return;

    try {
      const response = await eCourseService.reassignCourse(
        courseToReassign._id,
        selectedTeacherId
      );

      if (response.success) {
        setShowReassignModal(false);
        setCourseToReassign(null);
        setSelectedTeacherId("");
        fetchAllCourses();
        alert("Course reassigned successfully!");
      } else {
        setError("Failed to reassign course");
      }
    } catch (err) {
      console.error("Error reassigning course:", err);
      setError("Failed to reassign course");
    }
  };

  const filteredCourses = courses.filter(
    (course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor?.firstName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      course.instructor?.lastName
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published":
        return "bg-gradient-to-br from-emerald-300 to-green-400";
      case "approved":
        return "bg-gradient-to-br from-blue-300 to-blue-400";
      case "pending_approval":
        return "bg-gradient-to-br from-amber-300 to-orange-400";
      case "rejected":
        return "bg-gradient-to-br from-red-300 to-red-400";
      case "draft":
        return "bg-gradient-to-br from-yellow-300 to-yellow-400";
      case "archived":
        return "bg-gradient-to-br from-slate-300 to-slate-400";
      case "suspended":
        return "bg-gradient-to-br from-red-300 to-red-400";
      default:
        return "bg-gradient-to-br from-slate-300 to-slate-400";
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = getStatusColor(status);
    const displayStatus = status
      .replace("_", " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return (
      <span
        className={`px-3 py-1.5 rounded-xl text-xs font-bold text-white shadow-sm ${colors}`}
      >
        {displayStatus}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              E-Learning Course Management
            </h1>
            <p className="text-slate-600 font-medium">
              Manage all e-learning courses, approvals, and content
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search courses or instructors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:outline-none transition-all"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
          >
            <option value="all">All Status</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="published">Published</option>
            <option value="rejected">Rejected</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Course Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Courses</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {courses.length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">
                Pending Approval
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {courses.filter((c) => c.status === "pending_approval").length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-4 shadow-lg">
              <Clock className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Published</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {courses.filter((c) => c.status === "published").length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-4 shadow-lg">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Students</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {courses.reduce(
                  (total, course) => total + (course.stats?.totalStudents || 0),
                  0
                )}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-4 shadow-lg">
              <Users className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Course List */}
      {loading ? (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
            <p className="text-slate-600 font-medium">Loading courses...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-4 w-fit mx-auto mb-4">
              <AlertCircle className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              Error loading courses
            </h3>
            <p className="text-slate-600 font-medium mt-2">{error}</p>
            <button
              onClick={fetchAllCourses}
              className="mt-6 bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <AlertCircle className="h-5 w-5 text-blue-500" />
              <span className="text-slate-700">Try Again</span>
            </button>
          </div>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-4 w-fit mx-auto mb-4">
              <BookOpen className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              No courses found
            </h3>
            <p className="text-slate-600 font-medium mt-2">
              {courses.length === 0
                ? "No courses have been created yet."
                : "Try adjusting your search or filter criteria."}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <div
              key={course._id}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              <div className="p-6">
                {/* Status Badge and Actions */}
                <div className="flex justify-between items-start mb-4">
                  {getStatusBadge(course.status || "draft")}
                  <div className="relative">
                    <button
                      onClick={() =>
                        setShowActionMenu(
                          showActionMenu === course._id ? null : course._id
                        )
                      }
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {showActionMenu === course._id && (
                      <div className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl z-10 border border-slate-200/60 overflow-hidden">
                        <div className="py-2">
                          <button
                            onClick={() => {
                              setSelectedCourse(course);
                              setShowApprovalModal(true);
                              setShowActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 flex items-center gap-2"
                          >
                            <Eye className="text-blue-500" size={16} />
                            View Details
                          </button>
                          <button
                            onClick={() => {
                              window.location.href = `/admin/elearning/courses/${course._id}/edit`;
                              setShowActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 flex items-center gap-2"
                          >
                            <Edit className="text-amber-500" size={16} />
                            Edit Course
                          </button>
                          <button
                            onClick={() => {
                              handleReassignClick(course);
                              setShowActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 flex items-center gap-2"
                          >
                            <UserCheck className="text-purple-500" size={16} />
                            Reassign Teacher
                          </button>
                          {course.status === "approved" && (
                            <button
                              onClick={() => {
                                handlePublish(course._id);
                                setShowActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 flex items-center gap-2"
                            >
                              <Play className="text-emerald-500" size={16} />
                              Publish
                            </button>
                          )}
                          {course.status === "published" && (
                            <button
                              onClick={() => {
                                handleUnpublish(course._id);
                                setShowActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 flex items-center gap-2"
                            >
                              <Pause className="text-amber-500" size={16} />
                              Unpublish
                            </button>
                          )}
                          <div className="border-t border-slate-200 my-1"></div>
                          <button
                            onClick={() => {
                              handleDelete(course._id);
                              setShowActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Course Info */}
                <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                  {course.title}
                </h3>
                <p className="text-slate-600 font-medium text-sm mb-4 line-clamp-2">
                  {course.shortDescription || course.description}
                </p>

                {/* Instructor Info */}
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-3 mb-4 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-slate-700">
                      Instructor:
                    </span>
                    <span className="text-slate-600 font-medium">
                      {course.instructor?.firstName}{" "}
                      {course.instructor?.lastName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-slate-700">Category:</span>
                    <span className="text-slate-600 font-medium">
                      {course.category || "Other"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-slate-700">Level:</span>
                    <span className="text-slate-600 font-medium">
                      {course.level || "Beginner"}
                    </span>
                  </div>
                </div>

                {/* Course Meta */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-1.5">
                      <Users className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-sm font-bold text-slate-700">
                      {course.stats?.totalStudents || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-gradient-to-br from-blue-300 to-blue-400 rounded-lg p-1.5">
                      <Clock className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-sm font-bold text-slate-700">
                      {course.duration?.estimatedHours || 0}h{" "}
                      {course.duration?.estimatedMinutes || 0}m
                    </span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  {course.status === "pending_approval" && (
                    <>
                      <button
                        onClick={() => handleApprove(course._id)}
                        className="flex-1 bg-white border-2 border-slate-200 px-3 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1.5"
                      >
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        <span className="text-slate-700 text-sm">Approve</span>
                      </button>
                      <button
                        onClick={() => handleReject(course._id)}
                        className="flex-1 bg-white border-2 border-slate-200 px-3 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1.5"
                      >
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="text-slate-700 text-sm">Reject</span>
                      </button>
                    </>
                  )}
                  {course.status === "approved" && (
                    <button
                      onClick={() => handlePublish(course._id)}
                      className="flex-1 bg-white border-2 border-slate-200 px-3 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1.5"
                    >
                      <Play className="h-4 w-4 text-emerald-500" />
                      <span className="text-slate-700 text-sm">
                        Publish Course
                      </span>
                    </button>
                  )}
                  {(course.status === "approved" ||
                    course.status === "published") && (
                    <button
                      onClick={() =>
                        (window.location.href = `/admin/elearning/courses/${course._id}/manage`)
                      }
                      className="flex-1 bg-white border-2 border-slate-200 px-3 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1.5"
                    >
                      <BookOpen className="h-4 w-4 text-blue-500" />
                      <span className="text-slate-700 text-sm">
                        Manage Content
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Course Detail Modal */}
      {showApprovalModal && selectedCourse && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200/60 p-6 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2.5 shadow-lg">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    Course Details
                  </h2>
                  <p className="text-sm text-slate-600 font-medium">
                    {selectedCourse.title}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedCourse(null);
                  setApprovalNotes("");
                }}
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl p-2 transition-all"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Course Details */}
              <div>
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <div className="bg-gradient-to-br from-blue-300 to-blue-400 rounded-lg p-1.5">
                    <BookOpen className="h-4 w-4 text-white" />
                  </div>
                  Course Information
                </h3>
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Title
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedCourse.title}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Status
                      </p>
                      <div>
                        {getStatusBadge(selectedCourse.status || "draft")}
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                      Description
                    </p>
                    <p className="text-sm font-medium text-slate-700">
                      {selectedCourse.description}
                    </p>
                  </div>

                  {selectedCourse.shortDescription && (
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Short Description
                      </p>
                      <p className="text-sm font-medium text-slate-700">
                        {selectedCourse.shortDescription}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Instructor
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedCourse.instructor?.firstName}{" "}
                        {selectedCourse.instructor?.lastName}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Category
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedCourse.category}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Level
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedCourse.level}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Language
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedCourse.language}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                        Duration
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedCourse.duration?.estimatedHours}h{" "}
                        {selectedCourse.duration?.estimatedMinutes}m
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                      Students Enrolled
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-1.5">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <p className="text-lg font-bold text-slate-900">
                        {selectedCourse.stats?.totalStudents || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Approval History */}
              {selectedCourse.approvalHistory &&
                selectedCourse.approvalHistory.length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-1.5">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      Approval History
                    </h3>
                    <div className="space-y-2">
                      {selectedCourse.approvalHistory.map((history, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 border border-slate-200/60"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-bold text-slate-900 capitalize mb-1">
                                {history.action}
                              </p>
                              <p className="text-sm text-slate-600 font-medium">
                                By: {history.performedBy.firstName}{" "}
                                {history.performedBy.lastName}
                              </p>
                              {history.notes && (
                                <p className="text-sm text-slate-600 font-medium mt-2 bg-white/60 rounded-lg p-2">
                                  <span className="font-bold">Notes:</span>{" "}
                                  {history.notes}
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 font-semibold bg-white/60 rounded-lg px-2 py-1">
                              {new Date(
                                history.performedAt
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Approval Actions for Pending Courses */}
              {selectedCourse.status === "pending_approval" && (
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Approval Notes (Optional)
                  </label>
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:outline-none transition-all resize-none"
                    placeholder="Add any notes for the instructor..."
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedCourse(null);
                    setApprovalNotes("");
                  }}
                  className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <XCircle className="h-5 w-5 text-slate-500" />
                  <span className="text-slate-700">Close</span>
                </button>
                {selectedCourse.status === "pending_approval" && (
                  <>
                    <button
                      onClick={() => handleReject(selectedCourse._id)}
                      className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
                    >
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="text-slate-700">Reject Course</span>
                    </button>
                    <button
                      onClick={() => handleApprove(selectedCourse._id)}
                      className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
                    >
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      <span className="text-slate-700">Approve Course</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Course Modal */}
      {showReassignModal && courseToReassign && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200/60 p-6 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl p-2.5 shadow-lg">
                  <UserCheck className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  Reassign Course
                </h3>
              </div>
            </div>

            <div className="p-6">
              {/* Current Course Info */}
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 mb-6">
                <div className="mb-3">
                  <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                    Course
                  </p>
                  <p className="text-sm font-bold text-slate-900">
                    {courseToReassign.title}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                    Current Instructor
                  </p>
                  <p className="text-sm font-bold text-slate-900">
                    {courseToReassign.instructor?.firstName}{" "}
                    {courseToReassign.instructor?.lastName}
                  </p>
                </div>
              </div>

              {/* Select New Instructor */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Select New Instructor
                </label>
                <select
                  value={selectedTeacherId}
                  onChange={(e) => setSelectedTeacherId(e.target.value)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 focus:border-purple-400 focus:outline-none transition-all"
                  required
                >
                  <option value="">Choose an instructor...</option>
                  {teachers.map((teacher) => (
                    <option key={teacher._id} value={teacher._id}>
                      {teacher.firstName} {teacher.lastName} ({teacher.email}) -{" "}
                      {teacher.roles?.join(", ") || "No roles"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReassignModal(false);
                    setCourseToReassign(null);
                    setSelectedTeacherId("");
                  }}
                  className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <XCircle className="h-5 w-5 text-slate-500" />
                  <span className="text-slate-700">Cancel</span>
                </button>
                <button
                  onClick={handleReassignCourse}
                  disabled={!selectedTeacherId}
                  className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <UserCheck className="h-5 w-5 text-purple-500" />
                  <span className="text-slate-700">Reassign Course</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { AdminECourseApprovals };
